import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Upload, Shirt as ShirtIconLucide, ChevronUp, ChevronDown, Star } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ShirtIcon } from "../components/ShirtIcon";

const PATTERN_TYPES = [
  { id: "stripes_full", label: "Full-height stripes" },
  { id: "checkerboard", label: "Checkerboard" },
  { id: "chevron_sides", label: "Side chevron" },
  { id: "ringer", label: "Plain / ringer collar" },
  { id: "pinstripe", label: "Pinstripe" },
  { id: "custom_image", label: "Custom upload (SVG / image)" },
];

// Manages the named shirt designs available in the Classic Lineup
// Builder's shirt selector (see ShirtIcon.jsx / ClassicLineupBuilderPage.jsx).
// Every design — including the seeded "Classic (Sky Blue)" — can be
// renamed, re-imaged, reordered, set as default, or deleted; nothing is
// locked. Existing rows only offer a name + a single "replace image"
// button (pattern/color fields only exist on the "Add new" form).
export default function AdminClassicShirts() {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [savingId, setSavingId] = useState("");

  const [name, setName] = useState("");
  const [role, setRole] = useState("outfield");
  const [patternType, setPatternType] = useState("stripes_full");
  const [primaryColor, setPrimaryColor] = useState("#6CABDD");
  const [secondaryColor, setSecondaryColor] = useState("#FFFFFF");
  const [trimColor, setTrimColor] = useState("#002d75");
  const [imageUrl, setImageUrl] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rowUploadingId, setRowUploadingId] = useState("");
  const [movingId, setMovingId] = useState("");
  const [defaultingId, setDefaultingId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/classic-shirt-designs");
      setDesigns(r.data || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const uploadNewImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      if (r.data?.url) setImageUrl(r.data.url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setImageUploading(false);
      try { e.target.value = ""; } catch (_e) { /* ignore */ }
    }
  };

  const uploadRowImage = async (design, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setRowUploadingId(design.id);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      if (r.data?.url) {
        // Uploading a row's image always makes it a custom-image shirt —
        // there's no separate pattern picker per row any more, so this is
        // the only way (and the implicit result) of setting one.
        setDesigns((prev) => prev.map((d) => (
          d.id === design.id ? { ...d, image_url: r.data.url, pattern_type: "custom_image" } : d
        )));
        try {
          await api.put(`/admin/classic-shirt-designs/${design.id}`, {
            name: design.name,
            role: design.role || "outfield",
            pattern_type: "custom_image",
            primary_color: design.primary_color,
            secondary_color: design.secondary_color,
            trim_color: design.trim_color,
            image_url: r.data.url,
          });
          setSuccess(`Updated "${design.name}"'s shirt image`);
        } catch (err) {
          setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRowUploadingId("");
      try { e.target.value = ""; } catch (_e) { /* ignore */ }
    }
  };

  const updateDesignField = (id, key, value) => {
    setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, [key]: value } : d)));
  };

  const renameDesign = (id, newName) => {
    setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, name: newName } : d)));
  };

  const saveDesign = async (design) => {
    if (design.pattern_type === "custom_image" && !design.image_url) {
      setError("Please upload an image for a custom shirt design");
      return;
    }
    setSavingId(design.id);
    setError("");
    setSuccess("");
    try {
      await api.put(`/admin/classic-shirt-designs/${design.id}`, {
        name: design.name,
        role: design.role || "outfield",
        pattern_type: design.pattern_type,
        primary_color: design.primary_color,
        secondary_color: design.secondary_color,
        trim_color: design.trim_color,
        image_url: design.image_url || "",
      });
      setSuccess(`Saved "${design.name}"`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingId("");
    }
  };

  const moveDesign = async (id, direction) => {
    setMovingId(id);
    setError("");
    try {
      const r = await api.post(`/admin/classic-shirt-designs/${id}/move`, { direction });
      setDesigns(r.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setMovingId("");
    }
  };

  const setDefaultDesign = async (id) => {
    setDefaultingId(id);
    setError("");
    setSuccess("");
    try {
      const r = await api.post(`/admin/classic-shirt-designs/${id}/set-default`);
      setDesigns(r.data || []);
      setSuccess("Default shirt updated");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDefaultingId("");
    }
  };

  const deleteDesign = async (design) => {
    if (!window.confirm(`Delete "${design.name}"? Any lineup currently using it will fall back to the plain default shirt.`)) return;
    setSavingId(design.id);
    setError("");
    try {
      await api.delete(`/admin/classic-shirt-designs/${design.id}`);
      setDesigns((prev) => prev.filter((d) => d.id !== design.id));
      setSuccess(`Deleted "${design.name}"`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingId("");
    }
  };

  const createDesign = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please give the new shirt a name");
      return;
    }
    if (patternType === "custom_image" && !imageUrl) {
      setError("Please upload an image for a custom shirt design");
      return;
    }
    setCreating(true);
    setError("");
    setSuccess("");
    try {
      const r = await api.post("/admin/classic-shirt-designs", {
        name: name.trim(),
        role,
        pattern_type: patternType,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        trim_color: trimColor,
        image_url: patternType === "custom_image" ? imageUrl : "",
      });
      setDesigns((prev) => [...prev, r.data]);
      setName("");
      setImageUrl("");
      setSuccess(`Added "${r.data.name}"`);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setCreating(false);
    }
  };

  const outfieldDesigns = designs.filter((d) => (d.role || "outfield") !== "goalkeeper");
  const gkDesigns = designs.filter((d) => d.role === "goalkeeper");

  const renderDesignRow = (d, idx, group) => (
    <div key={d.id} className="flex flex-wrap items-center gap-4 px-4 py-4" data-testid={`classic-shirt-row-${d.id}`}>
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-black/10 bg-[#F8FAFC]">
        <ShirtIcon isGk={d.role === "goalkeeper"} showNumber={false} sizeClass="h-10 w-10" design={d} />
      </div>

      <input
        type="text"
        value={d.name}
        onChange={(e) => renameDesign(d.id, e.target.value)}
        data-testid={`classic-shirt-name-${d.id}`}
        className="min-w-[160px] flex-1 rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
      />

      <label className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-sm border border-dashed border-black/20 px-3 text-xs font-semibold hover:border-[#0A0F17]">
        {rowUploadingId === d.id ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
        {d.image_url ? "Replace image" : "Upload image"}
        <input
          type="file"
          accept="image/*,.svg"
          className="hidden"
          onChange={(e) => uploadRowImage(d, e)}
          disabled={rowUploadingId === d.id}
          data-testid={`classic-shirt-image-upload-${d.id}`}
        />
      </label>

      {d.is_default ? (
        <span
          data-testid={`classic-shirt-default-badge-${d.id}`}
          className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md border border-amber-400/40 bg-amber-50 px-2.5 text-xs font-bold text-amber-700"
        >
          <Star size={12} className="fill-amber-500 text-amber-500" /> Default
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setDefaultDesign(d.id)}
          disabled={defaultingId === d.id}
          data-testid={`classic-shirt-set-default-${d.id}`}
          className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md border border-black/10 px-2.5 text-xs font-semibold text-[var(--brand-body)] hover:border-[#0A0F17] hover:text-[var(--brand-ink)] disabled:opacity-60"
        >
          {defaultingId === d.id ? <Loader2 size={12} className="animate-spin" /> : <Star size={12} />} Set default
        </button>
      )}

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => moveDesign(d.id, "up")}
          disabled={idx === 0 || movingId === d.id}
          data-testid={`classic-shirt-move-up-${d.id}`}
          title="Move up"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-black/10 hover:bg-black/5 disabled:opacity-30"
        >
          <ChevronUp size={14} />
        </button>
        <button
          type="button"
          onClick={() => moveDesign(d.id, "down")}
          disabled={idx === group.length - 1 || movingId === d.id}
          data-testid={`classic-shirt-move-down-${d.id}`}
          title="Move down"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-black/10 hover:bg-black/5 disabled:opacity-30"
        >
          <ChevronDown size={14} />
        </button>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => saveDesign(d)}
          disabled={savingId === d.id}
          data-testid={`classic-shirt-save-${d.id}`}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-black/10 px-3 text-sm font-semibold hover:bg-black/5 disabled:opacity-60"
        >
          {savingId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
        </button>
        <button
          type="button"
          onClick={() => deleteDesign(d)}
          disabled={savingId === d.id}
          data-testid={`classic-shirt-delete-${d.id}`}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#E63946]/30 px-3 text-sm font-semibold text-[#E63946] hover:bg-[#E63946]/5 disabled:opacity-60"
        >
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]" data-testid="page-admin-classic-shirts">
      <Header />
      <main className="mx-auto max-w-3xl px-5 py-8 sm:px-8">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-body)] hover:text-[var(--brand-ink)]">
          <ArrowLeft size={14} /> Back to Admin
        </Link>

        <div className="mt-3 flex items-center gap-2 text-brand-accent">
          <ShirtIconLucide size={14} strokeWidth={2.5} />
          <span className="label-kicker">Classic Lineup Builder</span>
        </div>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tight sm:text-4xl">
          Classic shirt designs.
        </h1>
        <p className="mt-2 text-sm text-[var(--brand-body)]">
          These show up in the shirt selectors on the Classic Lineup Builder — Outfield
          designs apply to every outfield player, Goalkeeper designs apply only to the
          keeper (their own separate selector in the builder). Every design can be
          renamed, given a new image, reordered, deleted, or set as the default for its
          role (the shirt a fresh visit to the builder pre-selects) — nothing is locked.
        </p>

        {error ? (
          <p className="mt-4 rounded-md border border-[#E63946]/30 bg-[#E63946]/5 px-3 py-2 text-sm text-[#E63946]" data-testid="classic-shirts-error">
            {error}
          </p>
        ) : null}
        {success ? (
          <p className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700" data-testid="classic-shirts-success">
            {success}
          </p>
        ) : null}

        {/* Add new shirt */}
        <form onSubmit={createDesign} className="mt-6 rounded-md border border-black/10 bg-white p-4">
          <p className="label-kicker text-[var(--brand-body)] mb-3">Add a new shirt design</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-black/10 bg-[#F8FAFC]">
              <ShirtIcon
                isGk={role === "goalkeeper"}
                showNumber={false}
                sizeClass="h-12 w-12"
                design={{ pattern_type: patternType, primary_color: primaryColor, secondary_color: secondaryColor, trim_color: trimColor, image_url: imageUrl }}
              />
            </div>
            <div className="min-w-[160px] flex-1">
              <label className="block label-kicker text-[var(--brand-body)]">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. '70s Away (Brown)"
                data-testid="classic-shirts-new-name"
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Applies to</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="classic-shirts-new-role"
                className="mt-1 block rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              >
                <option value="outfield">Outfield players</option>
                <option value="goalkeeper">Goalkeeper only</option>
              </select>
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Pattern</label>
              <select
                value={patternType}
                onChange={(e) => setPatternType(e.target.value)}
                data-testid="classic-shirts-new-pattern"
                className="mt-1 block rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              >
                {PATTERN_TYPES.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
            {patternType === "custom_image" ? (
              <div>
                <label className="block label-kicker text-[var(--brand-body)]">Shirt artwork</label>
                <label className="mt-1 inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-sm border border-dashed border-black/20 px-3 text-sm font-semibold hover:border-[#0A0F17]">
                  {imageUploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {imageUrl ? "Replace" : "Upload SVG / image"}
                  <input
                    type="file"
                    accept="image/*,.svg"
                    className="hidden"
                    onChange={uploadNewImage}
                    disabled={imageUploading}
                    data-testid="classic-shirts-new-image-upload"
                  />
                </label>
              </div>
            ) : (
              <>
                <div>
                  <label className="block label-kicker text-[var(--brand-body)]">Primary</label>
                  <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="mt-1 h-9 w-12 rounded-sm border border-black/15" />
                </div>
                <div>
                  <label className="block label-kicker text-[var(--brand-body)]">Secondary</label>
                  <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="mt-1 h-9 w-12 rounded-sm border border-black/15" />
                </div>
              </>
            )}
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Trim</label>
              <input type="color" value={trimColor} onChange={(e) => setTrimColor(e.target.value)} className="mt-1 h-9 w-12 rounded-sm border border-black/15" />
            </div>
            <button
              type="submit"
              disabled={creating || imageUploading}
              data-testid="classic-shirts-new-save"
              className="inline-flex h-10 items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 text-sm font-bold text-white hover:bg-black disabled:opacity-60"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
            </button>
          </div>
        </form>

        {/* Existing designs — split into Outfield / Goalkeeper sections since
            each has its own selector + independent "default" in the builder. */}
        {loading ? (
          <div className="mt-6 rounded-md border border-black/10 bg-white p-6 text-sm text-[var(--brand-body)]">Loading…</div>
        ) : (
          <>
            <p className="mt-8 mb-2 label-kicker text-[var(--brand-body)]">Outfield shirts</p>
            <div className="divide-y divide-black/5 rounded-md border border-black/10 bg-white" data-testid="classic-shirts-outfield-list">
              {outfieldDesigns.length === 0 ? (
                <p className="p-6 text-sm text-[var(--brand-body)] italic">No outfield shirts yet.</p>
              ) : (
                outfieldDesigns.map((d, idx) => renderDesignRow(d, idx, outfieldDesigns))
              )}
            </div>

            <p className="mt-8 mb-2 label-kicker text-[var(--brand-body)]">Goalkeeper shirts</p>
            <div className="divide-y divide-black/5 rounded-md border border-black/10 bg-white" data-testid="classic-shirts-goalkeeper-list">
              {gkDesigns.length === 0 ? (
                <p className="p-6 text-sm text-[var(--brand-body)] italic">
                  No goalkeeper shirts yet — add one above with "Applies to" set to Goalkeeper only.
                </p>
              ) : (
                gkDesigns.map((d, idx) => renderDesignRow(d, idx, gkDesigns))
              )}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
